import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';
import { positionsService } from '@modules/organizationalStructure/positions/services/positions';
import { identificatorsService } from '@modules/additionalSettings/nomenclatures/services/identificators';
import LoadingButton from '@shared/components/LoadingButton';


import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function LevelOfStructuralForm({ onClose, onConfirm, editData, formProps, companies, companiesLoading, selectedCompany }) {

    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [isIndefinite, setIsIndefinite] = useState(false);
    const [parentValue, setParentValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState({
        workCategory: { options: [], loading: false },
        degreeLevel: { options: [], loading: false },
        categoryOfStaff: { options: [], loading: false },
        hrJobDescriptionId: { options: [], loading: false },
        nkpdClass: { options: [], loading: false },
        nkpdSubclass: { options: [], loading: false },
        nkpdGroup: { options: [], loading: false },
        nkpdUnitGroup: { options: [], loading: false },
        nkpd: { options: [], loading: false },
        position: { options: [], loading: false }
    });

    const validationSchema = yup.object().shape({
        empBranchId: yup.string().required(t('common.required')),
        jobCode: yup.string().required(t('common.required')),
        shortDescription: yup.string().required(t('common.required')),
        longDescription: yup.string().required(t('common.required')),
        indefinite: yup.boolean(),
        workTime: yup.string()
            .required(t('common.required'))
            .test('is-number', t('common.mustBeNumber'), value => {
                if (!value) return false;
                return !isNaN(Number(value));
            }),
        workCategory: yup.string().required(t('common.required')),
        nkpd: yup.string().required(t('common.required')),
    });

    const { register, handleSubmit, setValue, watch, formState: { errors }, reset, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        defaultValues: {
            empBranchId: selectedCompany || '',
            jobCode: '',
            shortDescription: '',
            longDescription: '',
            workTime: '',
            minSalary: '',
            maxSalary: '',
            addPayedLeave: '',
            minProfServiceYears: '',
            minProfServiceMonths: '',
            workCategory: '',
            degreeLevelId: '',
            nkpd: '',
            openDate: new Date(),
            closeDate: '',
            indefinite: true
        }
    });

    // Watch values for controlled components
    const watchedEmpBranchId = watch('empBranchId');
    const watchedJobCode = watch('jobCode');
    const watchedShortDescription = watch('shortDescription');
    const watchedLongDescription = watch('longDescription');
    const watchedWorkTime = watch('workTime');
    const watchedMinSalary = watch('minSalary');
    const watchedMaxSalary = watch('maxSalary');
    const watchedAddPayedLeave = watch('addPayedLeave');
    const watchedMinProfServiceMonths = watch('minProfServiceMonths');
    const watchedMinProfServiceYears = watch('minProfServiceYears');
    const watchedDegreeLevel = watch('degreeLevelId');
    const watchedWorkCategory = watch('workCategory');
    const watchedCategoryOfStaff = watch('categoryOfStaff');
    const watchedHrJobDescriptionId = watch('hrJobDescriptionId');
    const watchedIsLeaderPosition = watch('leaderPosition');
    const watchedNkpd = watch('nkpd');
    const watchedNkpdClass = watch('nkpdClass');
    const watchedNkpdSubclass = watch('nkpdSubclass');
    const watchedNkpdGroup = watch('nkpdGroup');
    const watchedNkpdUnitGroup = watch('nkpdUnitGroup');
    const watchedPosition = watch('nkpd');
    const watchedOpenDate = watch('openDate');
    const watchedCloseDate = watch('closeDate');
    const watchedIndefinite = watch('indefinite');

    useEffect(() => {
        setIsIndefinite(watchedIndefinite);
        if (watchedIndefinite) {
            setValue('closeDate', '2099-12-31');
        } else if (watchedCloseDate === '2099-12-31') {
            setValue('closeDate', '');
        }
        trigger('closeDate');
    }, [watchedIndefinite, setValue, trigger]);

    const mnemonicMapping = {
        degreeLevel: "!EDUCATION",
        workCategory: '!CATWORK',
    };

    const fetchDropdownOptions = async (fieldName) => {
        const mnemonic = mnemonicMapping[fieldName];
        if (!mnemonic) {
            return;
        }

        try {
            setDropdownOptions(prev => ({
                ...prev,
                [fieldName]: { ...prev[fieldName], loading: true }
            }));

            const data = await positionsService.getNomenclaturesByMnemonic(mnemonic);

            if (!Array.isArray(data)) {
                setDropdownOptions(prev => ({
                    ...prev,
                    [fieldName]: { options: [], loading: false }
                }));
                return;
            }

            const options = data.map(item => ({
                value: item.value, 
                label: `${item.value} - ${item.note}`,
                recordValue: item.value
            }));


            setDropdownOptions(prev => ({
                ...prev,
                [fieldName]: { options, loading: false }
            }));
        } catch (error) {
            setDropdownOptions(prev => ({
                ...prev,
                [fieldName]: { options: [], loading: false }
            }));
        }
    };

    const fetchDegreeLevelOptions = async () => {
        try {
            const data = await positionsService.getDegreeLevelList();
            return data;
        } catch (error) {
            console.error('Error fetching degree level options:', error);
        }
    }
    const fetchPositionTypeOptions = async () => {
        try {
            const data = await positionsService.getPositionType();
            return data;
        } catch (error) {
            console.error('Error fetching position type options:', error);
        }
    }

    const fetchNkpdOptions = async (id) => {
        try {
            const data = await identificatorsService.getNomenclatures(id, parentValue);
            return data;
        } catch (error) {
            console.error('Error fetching nkpd options:', error);
        }
    }

    const fetchPositionsFileOptions = async () => {
        try {
            const data = await positionsService.getPositionsFile(selectedCompany);
            return data;
        } catch (error) {
            console.error('Error fetching positions file options:', error);
        }
    }

    useEffect(() => {
        Object.keys(mnemonicMapping).forEach(fieldName => {
            if (fieldName !== 'degreeLevel') {
                fetchDropdownOptions(fieldName);
            }
        });

        const loadDegreeLevelOptions = async () => {
            try {
                setDropdownOptions(prev => ({
                    ...prev,
                    degreeLevel: { ...prev.degreeLevel, loading: true }
                }));

                const data = await fetchDegreeLevelOptions();

                if (!Array.isArray(data)) {
                    setDropdownOptions(prev => ({
                        ...prev,
                        degreeLevel: { options: [], loading: false }
                    }));
                    return;
                }

                const options = data.map(item => ({
                    value: item.id,
                    label: `${item.code} - ${item.education}${item.degreeLevel ? ' - ' + item.degreeLevel : ''}`
                }));

                setDropdownOptions(prev => ({
                    ...prev,
                    degreeLevel: { options, loading: false }
                }));
            } catch (error) {
                setDropdownOptions(prev => ({
                    ...prev,
                    degreeLevel: { options: [], loading: false }
                }));
            }
        };
        loadDegreeLevelOptions();



        const loadNkpdOptions = async () => {
            try {
                setDropdownOptions(prev => ({
                    ...prev,
                    nkpdClass: { ...prev.nkpdClass, loading: true }
                }));

                const data = await fetchNkpdOptions(2);

                if (!Array.isArray(data)) {
                    setDropdownOptions(prev => ({
                        ...prev,
                        nkpdClass: { options: [], loading: false }
                    }));
                    return;
                }

                const options = data.map(item => {
                    return {
                        value: item.value,
                        label: `${item.value} - ${item.note}`
                    };
                });

                setDropdownOptions(prev => ({
                    ...prev,
                    nkpdClass: { options, loading: false }
                }));
            } catch (error) {
                setDropdownOptions(prev => ({
                    ...prev,
                    nkpdClass: { options: [], loading: false }
                }));
            }
        };
        loadNkpdOptions();

        const loadPositionsFileOptions = async (companyId) => {
            try {
                setDropdownOptions(prev => ({
                    ...prev,
                    hrJobDescriptionId: { ...prev.hrJobDescriptionId, loading: true }
                }));

                const data = await fetchPositionsFileOptions(companyId);
                let options = data.map(item => ({
                    value: item.id,
                    label: item.fileName
                }));

                // If editing and current hrJobDescription is not in the list, add it
                if (editData && editData.hrJobDescription) {
                    const currentExists = options.some(opt => opt.value === editData.hrJobDescription.id);
                    if (!currentExists) {
                        options = [
                            {
                                value: editData.hrJobDescription.id,
                                label: editData.fileName
                            },
                            ...options
                        ];
                    }
                }

                setDropdownOptions(prev => ({
                    ...prev,
                    hrJobDescriptionId: { options, loading: false }
                }));
            } catch (error) {
                console.error('Error fetching positions file options:', error);
                setDropdownOptions(prev => ({
                    ...prev,
                    hrJobDescriptionId: { options: [], loading: false }
                }));
            }
        };
        loadPositionsFileOptions(selectedCompany);
    }, []);

    useEffect(() => {
        const loadCategoryOfStaffOptions = async () => {
            try {
                setDropdownOptions(prev => ({
                    ...prev,
                    categoryOfStaff: { ...prev.categoryOfStaff, loading: true }
                }));
                const data = await identificatorsService.getNomenclatures(7, parentValue);

                // Add this part to set the options in state:
                setDropdownOptions(prev => ({
                    ...prev,
                    categoryOfStaff: {
                        loading: false,
                        options: data.map(item => ({
                            value: item.value,  // or whatever the id field is
                            label: `${item.value} - ${item.note}` // or whatever the name field is
                        }))
                    }
                }));

            } catch (error) {
                console.error('Error fetching category of staff options:', error);
                setDropdownOptions(prev => ({
                    ...prev,
                    categoryOfStaff: { loading: false, options: [] }
                }));
            }
        }

        loadCategoryOfStaffOptions();
    }, [parentValue]);

    useEffect(() => {
        if (watchedNkpdClass && dropdownOptions.categoryOfStaff.options.length > 0) {
            const matchingOption = dropdownOptions.categoryOfStaff.options.find(
                option => option.value === watchedNkpdClass
            );

            if (matchingOption) {
                setValue('categoryOfStaff', matchingOption.value);
            } else {
                setValue('categoryOfStaff', '');
            }
        }
        else {
            setValue('categoryOfStaff', '');
        }
    }, [watchedNkpdClass, dropdownOptions.categoryOfStaff.options, setValue]);


    useEffect(() => {
        if (editData) {

            // Handle empBranch object from API response - extract ID from nested structure
            let empBranchId = editData.empBranchId;
            if (!empBranchId && editData.empBranch && companies && companies.length > 0) {
                // Find company by name from the empBranch object
                const matchingCompany = companies.find(c => c.name === editData.empBranch.name);
                empBranchId = matchingCompany ? matchingCompany.id : (companies.length > 0 ? companies[0].id : null);
            } else if (!empBranchId && companies && companies.length > 0) {
                empBranchId = companies[0].id;
            }

            // Set all form values from editData
            Object.keys(editData).forEach(key => {
                // Skip nested objects that aren't form fields
                if (key !== 'empBranch' && key !== 'hrJobTrls' && key !== 'hrJobRights' &&
                    key !== 'createdBy' && key !== 'updatedBy' && key !== 'createdAt' &&
                    key !== 'updatedAt' && key !== 'empcontractses' && key !== 'hrJobTypes' &&
                    key !== 'hrJobDescription' && key !== 'degreeLevelId' && key !== 'priority') {
                    setValue(key, editData[key]);
                }
            });

            if (empBranchId) {
                setValue('empBranchId', empBranchId);
            }

            const isIndefiniteRecord = editData.indefinite || editData.closeDate === '2099-12-31';
            setValue('indefinite', isIndefiniteRecord);
            setIsIndefinite(isIndefiniteRecord);

            if (editData.isLeaderPosition) {
                const mappedValue = editData.isLeaderPosition;
                setValue('leaderPosition', mappedValue);
            }

            if (editData.degreeLevels && editData.degreeLevels.id) {
                setValue('degreeLevelId', editData.degreeLevels.id);
            }


            if (editData.hrJobDescription && editData.hrJobDescription.id) {
                setValue('hrJobDescriptionId', editData.hrJobDescription.id);
            }

            const loadNkpdHierarchy = async () => {
                try {
                    // Fetch NKPD hierarchy for the position
                    const hierarchyData = await positionsService.getNkpdHierarchy(editData.nkpd);

                    if (Array.isArray(hierarchyData) && hierarchyData.length > 0) {
                        // Map hierarchy levels to dropdowns based on value length/position
                        const hierarchyLevels = hierarchyData.sort((a, b) => a.value.length - b.value.length);
                        (hierarchyLevels);

                        if (hierarchyLevels.length >= 1) {
                            const classLevel = hierarchyLevels[0];
                            setValue('nkpdClass', classLevel.value);
                            setParentValue(classLevel.value);
                        }

                        if (hierarchyLevels.length >= 2) {
                            // Level 2: Subclass (nkpdSubclass) - usually 2-3 digits
                            const subclassLevel = hierarchyLevels[1];
                            setValue('nkpdSubclass', subclassLevel.value);

                            // Load subclass options
                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdSubclass: { ...prev.nkpdSubclass, loading: true }
                            }));

                            const subclassData = await identificatorsService.getNomenclatures(2, hierarchyLevels[0].value);
                            const subclassOptions = subclassData.map(item => ({
                                value: item.value,
                                label: `${item.value} - ${item.note}`
                            }));

                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdSubclass: { options: subclassOptions, loading: false }
                            }));
                        }

                        if (hierarchyLevels.length >= 3) {
                            // Level 3: Group (nkpdGroup) - usually 3-4 digits
                            const groupLevel = hierarchyLevels[2];
                            setValue('nkpdGroup', groupLevel.value);

                            // Load group options
                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdGroup: { ...prev.nkpdGroup, loading: true }
                            }));

                            const groupData = await identificatorsService.getNomenclatures(2, hierarchyLevels[1].value);
                            const groupOptions = groupData.map(item => ({
                                value: item.value,
                                label: `${item.value} - ${item.note}`
                            }));

                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdGroup: { options: groupOptions, loading: false }
                            }));
                        }

                        if (hierarchyLevels.length >= 4) {
                            // Level 4: Unit Group (nkpdUnitGroup) - usually 4-5 digits
                            const unitGroupLevel = hierarchyLevels[3];
                            setValue('nkpdUnitGroup', unitGroupLevel.value);

                            // Load unit group options
                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdUnitGroup: { ...prev.nkpdUnitGroup, loading: true }
                            }));

                            const unitGroupData = await identificatorsService.getNomenclatures(2, hierarchyLevels[2].value);
                            const unitGroupOptions = unitGroupData.map(item => ({
                                value: item.value,
                                label: `${item.value} - ${item.note}`
                            }));

                            setDropdownOptions(prev => ({
                                ...prev,
                                nkpdUnitGroup: { options: unitGroupOptions, loading: false }
                            }));
                        }

                        if (hierarchyLevels.length >= 5) {
                            // Level 5: Position (nkpd) - usually 6+ digits
                            const positionLevel = hierarchyLevels[4];

                            // Load position options
                            setDropdownOptions(prev => ({
                                ...prev,
                                position: { ...prev.position, loading: true }
                            }));

                            const positionData = await identificatorsService.getNomenclatures(2, hierarchyLevels[3].value);
                            const positionOptions = positionData.map(item => ({
                                value: item.id,
                                label: `${item.value} - ${item.note}`
                            }));

                            setDropdownOptions(prev => ({
                                ...prev,
                                position: { options: positionOptions, loading: false }
                            }));

                            const selectedPosition = positionData.find((item) =>
                                String(item.value) === String(positionLevel.value)
                                || String(item.id) === String(positionLevel.value)
                                || String(item.id) === String(editData.nkpd)
                                || String(item.value) === String(editData.nkpd)
                            );
                            setValue('nkpd', selectedPosition?.id ?? positionLevel.value);
                        }

                        // Ensure position dropdown is populated even if hierarchy processing stops early
                        if (hierarchyLevels.length >= 4 && !dropdownOptions.position.options.length) {
                            setDropdownOptions(prev => ({
                                ...prev,
                                position: { ...prev.position, loading: true }
                            }));

                            const positionData = await identificatorsService.getNomenclatures(2, hierarchyLevels[3].value);
                            const positionOptions = positionData.map(item => ({
                                value: item.id,
                                label: `${item.value} - ${item.note}`
                            }));

                            setDropdownOptions(prev => ({
                                ...prev,
                                position: { options: positionOptions, loading: false }
                            }));

                            const selectedPosition = positionData.find((item) =>
                                String(item.id) === String(editData.nkpd)
                                || String(item.value) === String(editData.nkpd)
                            );
                            if (selectedPosition) {
                                setValue('nkpd', selectedPosition.id);
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ Error loading NKPD hierarchy for edit:', error);

                    const loadCascadingDropdowns = async () => {
                        try {
                            if (editData.nkpdClass) {
                                setParentValue(editData.nkpdClass);
                                setDropdownOptions(prev => ({
                                    ...prev,
                                    nkpdSubclass: { ...prev.nkpdSubclass, loading: true }
                                }));

                                const subclassData = await identificatorsService.getNomenclatures(2, editData.nkpdClass);

                                const subclassOptions = subclassData.map(item => ({
                                    value: item.value,
                                    label: item.note
                                }));

                                setDropdownOptions(prev => ({
                                    ...prev,
                                    nkpdSubclass: { options: subclassOptions, loading: false }
                                }));

                                if (editData.nkpdSubclass) {
                                    setDropdownOptions(prev => ({
                                        ...prev,
                                        nkpdGroup: { ...prev.nkpdGroup, loading: true }
                                    }));

                                    const groupData = await identificatorsService.getNomenclatures(2, editData.nkpdSubclass);

                                    const groupOptions = groupData.map(item => ({
                                        value: item.value,
                                        label: item.note
                                    }));

                                    setDropdownOptions(prev => ({
                                        ...prev,
                                        nkpdGroup: { options: groupOptions, loading: false }
                                    }));

                                    if (editData.nkpdGroup) {
                                        setDropdownOptions(prev => ({
                                            ...prev,
                                            nkpdUnitGroup: { ...prev.nkpdUnitGroup, loading: true }
                                        }));

                                        const unitGroupData = await identificatorsService.getNomenclatures(2, editData.nkpdGroup);

                                        const unitGroupOptions = unitGroupData.map(item => ({
                                            value: item.value,
                                            label: item.note
                                        }));

                                        setDropdownOptions(prev => ({
                                            ...prev,
                                            nkpdUnitGroup: { options: unitGroupOptions, loading: false }
                                        }));

                                        if (editData.nkpdUnitGroup) {
                                            setDropdownOptions(prev => ({
                                                ...prev,
                                                position: { ...prev.position, loading: true }
                                            }));

                                            const positionData = await identificatorsService.getNomenclatures(2, editData.nkpdUnitGroup);

                                            const positionOptions = positionData.map(item => ({
                                                value: item.id,
                                                label: `${item.value} - ${item.note}`
                                            }));

                                            setDropdownOptions(prev => ({
                                                ...prev,
                                                position: { options: positionOptions, loading: false }
                                            }));

                                            const selectedPosition = positionData.find((item) =>
                                                String(item.id) === String(editData.nkpd)
                                                || String(item.value) === String(editData.nkpd)
                                            );
                                            if (selectedPosition) {
                                                setValue('nkpd', selectedPosition.id);
                                            }
                                        }
                                    }
                                }
                            }
                        } catch (fallbackError) {
                            console.error('❌ Error loading cascading dropdowns for edit:', fallbackError);
                        }
                    };

                    loadCascadingDropdowns();
                }
            };

            loadNkpdHierarchy();
        } else {
            reset();
            setIsIndefinite(true);
            if (selectedCompany) {
                setValue('empBranchId', selectedCompany);
            }
        }
    }, [editData, setValue, reset, selectedCompany, companies]);

    const submitForm = async (data) => {
        try {
            setLoading(true);

            const { closeDate, indefinite, ...formDataWithoutCloseDate } = data;
            const finalCloseDate = indefinite ? '2099-12-31' : closeDate;

            const formData = {
                ...formDataWithoutCloseDate,
                closeDate: finalCloseDate,
                indefinite: indefinite
            };

            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            console.error('Error saving position:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="level-of-structural-form-container">
            <div className="modal show" tabIndex="-1">
                <div className="height-97" ref={modalRef}>

                    <div className="modal-content height-90">
                        <button type="button" className="close" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <h3 className="form-subtitle">
                            {editData ? t('common.edit') : t('common.add')} {t('organizationalStructure.positions.form.title')}
                        </h3>
                        <div className="modal-body height-100" style={{ alignItems: 'flex-start' }}>
                            <form id="structuralLevelForm" onSubmit={handleSubmit(submitForm)}>
                                <div className="flex-column">
                                    <div className="flex-row">
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.company')}<span className="required">*</span></span>
                                            <select
                                                className="small-select"
                                                value={watchedEmpBranchId || ''}
                                                {...register('empBranchId', {
                                                    required: true,
                                                    onChange: () => trigger('empBranchId')
                                                })}
                                                disabled={!!editData || companiesLoading}
                                            >
                                                <option value="">Изберете компания</option>
                                                {companies.map(company => (
                                                    <option key={company.id} value={company.id}>
                                                        {company.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.empBranchId && <p className="error-message compac no-wrap">{errors.empBranchId.message}</p>}
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold">{t('organizationalStructure.positions.form.code')}<span className="required">*</span></span>
                                            <input
                                                type="text"
                                                className={`small-input ${errors.jobCode ? 'input-error' : ''}`}
                                                value={watchedJobCode || ''}
                                                placeholder={
                                                    errors.jobCode
                                                        ? errors.jobCode.message
                                                        : t('common.required')
                                                }
                                                {...register('jobCode', {
                                                    required: t('common.fieldRequired'),
                                                    onChange: () => trigger('jobCode'),
                                                })}
                                                maxLength={10}
                                            />
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.workTime')}<span className="required">*</span></span>
                                            <input
                                                type="number"
                                                min="0"
                                                style={{ width: '40%', }}
                                                className={`small-input ${errors.workTime ? 'input-error' : ''}`}
                                                value={watchedWorkTime || ''}
                                                placeholder={errors.workTime ? errors.workTime.message : t('common.required')}
                                                maxLength={2}
                                                {...register('workTime')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 2) {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-row margin-bottom-5">
                                        <span className="span-bold">{t('organizationalStructure.positions.form.abbreviation')}<span className="required">*</span></span>
                                        <input
                                            type="text"
                                            maxLength={20}
                                            className={`small-input ${errors.shortDescription ? 'input-error' : ''}`}
                                            placeholder={errors.shortDescription ? errors.shortDescription.message : t('common.required')}
                                            value={watchedShortDescription || ''}
                                            {...register('shortDescription', {
                                                required: true,
                                                onChange: () => trigger('shortDescription')
                                            })}
                                        />
                                    </div>
                                    <div className="flex-row margin-bottom-5">
                                        <span className="span-bold">{t('organizationalStructure.positions.table.headers.title')}<span className="required">*</span></span>
                                        <input
                                            type="text"
                                            className={`small-input ${errors.longDescription ? 'input-error' : ''}`}
                                            placeholder={errors.longDescription ? errors.longDescription.message : t('common.required')}
                                            value={watchedLongDescription || ''}
                                            {...register('longDescription', {
                                                required: true,
                                                onChange: () => trigger('longDescription')
                                            })}
                                        />
                                    </div>
                                    <div className="flex-row width-100 margin-bottom-5">
                                        <div className="flex-row width-50">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.hrLaborCategoryId')} <span className="required">*</span></span>
                                            <select
                                                className={`small-select ${errors.workCategory ? 'error' : ''}`}
                                                value={watchedWorkCategory || ''}
                                                {...register('workCategory')}
                                                disabled={dropdownOptions.workCategory.loading}
                                            >
                                                <option value="">{dropdownOptions.workCategory.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.workCategory.options.map(option => {
                                                    return (
                                                        <option key={option.value} value={option.label}>{option.label}</option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div className="flex-row width-50">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.categoryOfStaff')}</span>
                                            <select
                                                className="small-select"
                                                value={watchedCategoryOfStaff || ''}
                                                {...register('categoryOfStaff')}
                                                disabled={true}
                                            >
                                                <option value="">{dropdownOptions.categoryOfStaff.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.categoryOfStaff.options.map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex-row margin-bottom-5">
                                        <div className="flex-row">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.minSalary')}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="small-input"
                                                value={watchedMinSalary || ''}
                                                maxLength={10}
                                                {...register('minSalary')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 10) {
                                                        e.target.value = e.target.value.slice(0, 10);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.maxSalary')}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="small-input"
                                                value={watchedMaxSalary || ''}
                                                maxLength={10}
                                                {...register('maxSalary')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 10) {
                                                        e.target.value = e.target.value.slice(0, 10);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.addPayedLeave')}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="small-input"
                                                value={watchedAddPayedLeave || ''}
                                                maxLength={2}
                                                {...register('addPayedLeave')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 2) {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row width-20">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.isLeaderPosition')}</span>
                                            <select
                                                className="small-select"
                                                value={watchedIsLeaderPosition || ''}
                                                {...register('leaderPosition')}
                                            >
                                                <option value="false">{t('common.no')}</option>
                                                <option value="true">{t('common.yes')}</option>
                                            </select>
                                        </div>
                                    </div>



                                    <div className="flex-row margin-bottom-5">
                                        <div className="flex-row width-30">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.minProfServiceYears')}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="small-input"
                                                value={watchedMinProfServiceYears || ''}
                                                maxLength={2}
                                                {...register('minProfServiceYears')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 2) {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row width-30">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.minProfServiceMonths')}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="small-input"
                                                value={watchedMinProfServiceMonths || ''}
                                                maxLength={2}
                                                {...register('minProfServiceMonths')}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 2) {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex-row width-40">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.degreeLevel')}</span>
                                            <select
                                                className="small-select"
                                                value={watchedDegreeLevel || ''}
                                                {...register('degreeLevelId')}
                                                disabled={dropdownOptions.degreeLevel.loading}
                                            >
                                                <option value="">{dropdownOptions.degreeLevel.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.degreeLevel.options.map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="flex-row margin-bottom-5">
                                        <span className="span-bold no-wrap"> Валидност</span>
                                        <span className="span-bold no-wrap" style={{ marginLeft: '10px' }}> {t('organizationalStructure.positions.form.indefinite')}
                                            <input
                                                style={{ marginLeft: '10px' }}
                                                type="checkbox"
                                                id="indefinite"
                                                {...register('indefinite')}
                                            />
                                        </span>
                                        <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.fromDate')}</span>
                                        <CustomDatePicker
                                            useDefaultSize={true}
                                            name="openDate"
                                            value={watch('openDate')}
                                            onChange={(date) => setValue('openDate', date)}
                                            placeholderText={t('common.dateFormat')}
                                            maxDate={new Date()}

                                        />
                                        {errors.openDate && <p className="error-message compact no-wrap">{errors.openDate.message}</p>}
                                        {!watchedIndefinite && (
                                            <>
                                                <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.toDate')}</span>
                                                <CustomDatePicker
                                                    useDefaultSize={true}
                                                    name="closeDate"
                                                    value={watch('closeDate') === '2099-12-31' ? '' : watch('closeDate')}
                                                    minDate={watch('openDate') || new Date()}
                                                    onChange={(date) => {
                                                        setValue('closeDate', date);
                                                        if (date && watchedIndefinite) {
                                                            setValue('indefinite', false);
                                                        }
                                                        trigger('closeDate');
                                                    }}
                                                    placeholderText={t('common.dateFormat')}
                                                />
                                                {errors.closeDate && <p className="error-message compact no-wrap">{errors.closeDate.message}</p>}
                                            </>
                                        )}

                                    </div>
                                    <div className="flex-column" style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '10px', borderColor: '#ccc' }}>
                                        <h4 className="form-subtitle text-center">{t('organizationalStructure.positions.form.nkpd')}</h4>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold">{t('organizationalStructure.positions.form.class')}<span className="required">*</span></span>
                                            <select
                                                className={`small-select ${errors.nkpd ? 'error' : ''}`}
                                                value={watchedNkpdClass || ''}
                                                {...register('nkpdClass')}
                                                disabled={dropdownOptions.nkpdClass.loading}
                                                onChange={async (e) => {
                                                    const value = e.target.value;
                                                    setValue('nkpdClass', value);
                                                    setParentValue(value);

                                                    // Clear child dropdowns
                                                    setDropdownOptions(prev => ({
                                                        ...prev,
                                                        nkpdSubclass: { options: [], loading: false },
                                                        nkpdGroup: { options: [], loading: false },
                                                        nkpdUnitGroup: { options: [], loading: false },
                                                        position: { options: [], loading: false }
                                                    }));

                                                    // Clear form values for child dropdowns
                                                    setValue('nkpdSubclass', '');
                                                    setValue('nkpdGroup', '');
                                                    setValue('nkpdUnitGroup', '');
                                                    // setValue('position', '');
                                                    setValue('nkpd', "");

                                                    // If a value is selected, fetch subclass options
                                                    if (value) {
                                                        try {
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdSubclass: { ...prev.nkpdSubclass, loading: true }
                                                            }));

                                                            const data = await identificatorsService.getNomenclatures(2, value);
                                                            const options = data.map((item, index) => ({
                                                                value: item.value,
                                                                label: `${item.value} - ${item.note}`
                                                            }));

                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdSubclass: { options, loading: false }
                                                            }));

                                                        } catch (error) {
                                                            console.error('❌ [NKPD] Error loading subclass options:', error);
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdSubclass: { options: [], loading: false }
                                                            }));
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">{dropdownOptions.nkpdClass.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.nkpdClass.options.map((option, index) => (
                                                    <option key={`nkpd-${option.value}-${index}`} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold">{t('organizationalStructure.positions.form.subclass')}</span>
                                            <select
                                                className={`small-select ${errors.nkpd ? 'error' : ''}`}
                                                value={watchedNkpdSubclass || ''}
                                                disabled={dropdownOptions.nkpdSubclass.loading}
                                                onChange={async (e) => {
                                                    const value = e.target.value;
                                                    setValue('nkpdSubclass', value);

                                                    // Clear child dropdowns
                                                    setDropdownOptions(prev => ({
                                                        ...prev,
                                                        nkpdGroup: { options: [], loading: false },
                                                        nkpdUnitGroup: { options: [], loading: false },
                                                        position: { options: [], loading: false }
                                                    }));

                                                    // Clear form values for child dropdowns
                                                    setValue('nkpdGroup', '');
                                                    setValue('nkpdUnitGroup', '');
                                                    setValue('position', '');

                                                    // If a value is selected, fetch group options
                                                    if (value) {
                                                        try {
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdGroup: { ...prev.nkpdGroup, loading: true }
                                                            }));

                                                            const data = await identificatorsService.getNomenclatures(2, value);
                                                            const options = data.map((item, index) => ({
                                                                value: item.value,
                                                                label: `${item.value} - ${item.note}`
                                                            }));

                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdGroup: { options, loading: false }
                                                            }));

                                                        } catch (error) {
                                                            console.error('❌ [NKPD] Error loading group options:', error);
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdGroup: { options: [], loading: false }
                                                            }));
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">{dropdownOptions.nkpdSubclass.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.nkpdSubclass.options.map((option, index) => (
                                                    <option key={`nkpdSubclass-${option.value}-${index}`} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold">{t('organizationalStructure.positions.form.group')}</span>
                                            <select
                                                className={`small-select ${errors.nkpd ? 'error' : ''}`}
                                                value={watchedNkpdGroup || ''}
                                                disabled={dropdownOptions.nkpdGroup.loading}
                                                onChange={async (e) => {
                                                    const value = e.target.value;
                                                    setValue('nkpdGroup', value);

                                                    // Clear child dropdowns
                                                    setDropdownOptions(prev => ({
                                                        ...prev,
                                                        nkpdUnitGroup: { options: [], loading: false },
                                                        position: { options: [], loading: false }
                                                    }));

                                                    // Clear form values for child dropdowns
                                                    setValue('nkpdUnitGroup', '');
                                                    setValue('position', '');

                                                    // If a value is selected, fetch unit group options
                                                    if (value) {
                                                        try {
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdUnitGroup: { ...prev.nkpdUnitGroup, loading: true }
                                                            }));

                                                            const data = await identificatorsService.getNomenclatures(2, value);
                                                            const options = data.map((item, index) => ({
                                                                value: item.value,
                                                                label: `${item.value} - ${item.note}`
                                                            }));

                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdUnitGroup: { options, loading: false }
                                                            }));

                                                        } catch (error) {
                                                            console.error('❌ [NKPD] Error loading unit group options:', error);
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                nkpdUnitGroup: { options: [], loading: false }
                                                            }));
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">{dropdownOptions.nkpdGroup.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.nkpdGroup.options.map((option, index) => (
                                                    <option key={`nkpdGroup-${option.value}-${index}`} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.unitGroup')}</span>
                                            <select
                                                className={`small-select ${errors.nkpd ? 'error' : ''}`}
                                                value={watchedNkpdUnitGroup || ''}
                                                disabled={dropdownOptions.nkpdUnitGroup.loading}
                                                onChange={async (e) => {
                                                    const value = e.target.value;
                                                    setValue('nkpdUnitGroup', value);
                                                    setDropdownOptions(prev => ({
                                                        ...prev,
                                                        position: { options: [], loading: false }
                                                    }));
                                                    setValue('position', '');
                                                    if (value) {
                                                        try {
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                position: { ...prev.position, loading: true }
                                                            }));

                                                            const data = await identificatorsService.getNomenclatures(2, value);
                                                            const options = data.map((item, index) => ({
                                                                value: item.id,
                                                                label: `${item.value} - ${item.note}`
                                                            }));

                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                position: { options, loading: false }
                                                            }));

                                                        } catch (error) {
                                                            console.error('❌ [NKPD] Error loading position options:', error);
                                                            setDropdownOptions(prev => ({
                                                                ...prev,
                                                                position: { options: [], loading: false }
                                                            }));
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">{dropdownOptions.nkpdUnitGroup.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.nkpdUnitGroup.options.map((option, index) => (
                                                    <option key={`nkpdUnitGroup-${option.value}-${index}`} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-row margin-bottom-5">
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.position')} <span className="required">*</span></span>
                                            <select
                                                className={`small-select ${errors.nkpd ? 'error' : ''}`}
                                                value={watchedPosition || ''}
                                                disabled={dropdownOptions.nkpd.loading}
                                                {...register('nkpd', {
                                                    required: t('common.requiredField'),
                                                    onChange: (e) => {
                                                        setValue('nkpd', e.target.value);
                                                        trigger('nkpd');
                                                    },
                                                })}
                                            >
                                                <option value="" disabled hidden>
                                                    {dropdownOptions.position.loading ? 'Зареждане...' : 'Изберете'}
                                                </option>
                                                {dropdownOptions.position.options.map((option, index) => (
                                                    <option key={`position-${option.value}-${index}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex-column">
                                        <h4 className="form-subtitle text-center">{t('organizationalStructure.positions.form.fileUpload')}</h4>
                                        <div className="flex-row margin-bottom-5" style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '10px', borderColor: '#ccc' }}>
                                            <span className="span-bold no-wrap">{t('organizationalStructure.positions.form.file')}</span>
                                            <select
                                                className="small-input"
                                                value={watchedHrJobDescriptionId || ''}
                                                {...register('hrJobDescriptionId')}
                                                disabled={dropdownOptions.hrJobDescriptionId.loading}
                                            >
                                                <option value="">{dropdownOptions.hrJobDescriptionId.loading ? 'Зареждане...' : 'Изберете'}</option>
                                                {dropdownOptions.hrJobDescriptionId.options.map((option, index) => (
                                                    <option key={`file-${option.value}-${index}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <LoadingButton type="submit" className="btn btn-primary" loading={loading} disabled={loading}>
                                        {t('common.save')}
                                    </LoadingButton>
                                    <button type="button" className="form-btn btn-red" onClick={onClose} disabled={loading}>
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div >
            </div >
        </div >
    );
}

